import { API_URL } from '../config/api';
/**
 * Image Upload Utilities - Local Storage
 * 
 * Sistema temporário de upload com preview local
 * Preparado para integração com storage definitivo (AWS S3, Firebase, etc.)
 * 
 * TODO: Integrar com storage cloud escolhido
 */

// Configurações de upload
// QA: SVG removido (backend rejeita por XSS — PR #203 de hardening).
// WebP adicionado pra bater com o que o input `accept=` declara.
const UPLOAD_CONFIG = {
  allowed_formats: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
  max_file_size: 5 * 1024 * 1024 // 5MB em bytes
};

/**
 * Upload de imagem para Cloudflare R2 via backend
 * 
 * @param {File} file - Arquivo de imagem a ser enviado
 * @param {Object} options - Opções adicionais (mantido para compatibilidade)
 * @returns {Promise<Object>} - Resultado do upload
 */
export const uploadImageToCloudinary = async (file, options = {}) => {
  try {
    // Validar arquivo
    if (!file) {
      throw new Error('Nenhum arquivo fornecido');
    }

    // Validar tamanho
    if (file.size > UPLOAD_CONFIG.max_file_size) {
      throw new Error(`Arquivo muito grande. Máximo ${UPLOAD_CONFIG.max_file_size / (1024 * 1024)}MB`);
    }

    // Validar formato
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!UPLOAD_CONFIG.allowed_formats.includes(fileExtension)) {
      throw new Error(`Formato não suportado. Use: ${UPLOAD_CONFIG.allowed_formats.join(', ')}`);
    }

    console.log('📁 Iniciando upload via backend:', file.name, '|', (file.size / 1024).toFixed(2), 'KB');

    // Preparar FormData para envio
    const formData = new FormData();
    formData.append('image', file);

    // Obter token de autenticação
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    // Fazer upload para o backend (rota isolada para teste)
    const response = await fetch(`${API_URL}/api/upload/isolated`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || errorData.message || 'Erro no upload');
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Erro no upload');
    }

    const uploadData = result.data;
    console.log('✅ Upload concluído para Cloudflare R2:', uploadData.url);

    return {
      success: true,
      url: uploadData.url,
      public_id: uploadData.key,
      width: null, // R2 não retorna dimensões automaticamente
      height: null,
      format: uploadData.mimeType,
      bytes: uploadData.size,
      created_at: uploadData.uploadedAt,
      // URLs (R2 não tem transformações automáticas, mas podemos implementar depois)
      thumbnail: uploadData.url,
      medium: uploadData.url,
      large: uploadData.url,
      // Metadata adicional
      provider: uploadData.provider,
      originalName: uploadData.originalName
    };

  } catch (error) {
    console.error('❌ Erro no upload para Cloudflare R2:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Remove uma imagem (temporário - só remove da memória)
 * TODO: Implementar deleção real quando integrar storage cloud
 * 
 * @param {string} publicId - ID da imagem a ser removida
 * @returns {Promise<Object>} - Resultado da operação
 */
export const deleteImageFromCloudinary = async (publicId) => {
  try {
    console.log('🗑️ Removendo imagem temporária:', publicId);
    
    // Por enquanto, só simula a remoção
    return {
      success: true,
      message: 'Imagem removida localmente (temporário)'
    };
    
  } catch (error) {
    console.error('❌ Erro ao remover imagem:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Cria preview local de uma imagem antes do upload
 * 
 * @param {File} file - Arquivo de imagem
 * @returns {Promise<string>} - Data URL para preview
 */
export const createImagePreview = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('Nenhum arquivo fornecido'));
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      resolve(e.target.result);
    };
    
    reader.onerror = () => {
      reject(new Error('Erro ao ler arquivo'));
    };
    
    reader.readAsDataURL(file);
  });
};

const imageUtils = {
  uploadImageToCloudinary, // Mantido nome para compatibilidade
  deleteImageFromCloudinary,
  createImagePreview,
  UPLOAD_CONFIG
};

export default imageUtils;
